/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState, useRef } from "react";
import { FormIsDirtyAlert } from "protocol-setup/components/FormIsDirtyAlertModal";
import RequestActionBar from "protocol-setup/components/RequestActionBar";
import { getRequestBarContent } from "protocol-setup/components/RequestActionBar/RequestBarHelper";
import {
  useProtocolSetupData,
  useValidateRequest
} from "protocol-setup/redux/useLovs";

import InactiveRequestRedirect from "protocol-setup/components/InactiveRequestRedirect";
import { useHistory, useParams } from "react-router";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { log, useSetupPermission } from "protocol-setup/helpers";
import ProtectionElements from "protocol-setup/helpers/ProtectionElement";
import RaveSetupForm from "./RaveSetupForm";
import InfoForm from "./InfoForm";
import OpenSetupForm from "./OpenSetupFrom";
import SignOffForms from "./SignOffForms";


const softValidationFields = [
  'requestType',
  'tentativeActivationDate',
  'protSetupContacts',
  'isStandardRandoNodeUrl',
  'isRaveProtocol'
];

function OpenRaveSetup() {
  const { rqNum } = useParams();
  const history = useHistory()
  const [isTabDirty, setDirtyTab] = useState(false);
  const [openModal, setModal] = useState(false);
  const lastFocusedElement = useRef(null);
  const { data: protocolSetupData, isLoading, trigger: fetchPsData } = useProtocolSetupData(rqNum, 'a', history);
  const [enabledTabs, setEnabledTabs] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [isFormReadOnly, setIsFormReadOnly] = useState(false);
  const { requestStatus, protocolNumber, requestNumber, requestType } = protocolSetupData || {};
  const { isSubmitFormEnabled } = useValidateRequest(protocolSetupData, "OPEN_RAVE", softValidationFields);
  const [requestBarContent, setRequestBarContent] = useState([]);

  const canSignoffView = useSetupPermission(ProtectionElements.SIGNOFF_VIEW);
  const canEditOpenRaveRequest = useSetupPermission(ProtectionElements.OPEN_REQUEST);
 
  useEffect(() => {
    if (!isLoading) {
      if (requestNumber) {
        setEnabledTabs(!!requestNumber && !!requestType);
        setTabIndex(0);
        const status = (requestStatus || "").toUpperCase();
        if (status && !["NEW", "DRAFT"].includes(status)) {
          setIsFormReadOnly(true);
        } else {
          setIsFormReadOnly(false);
        }
        setRequestBarContent(
          getRequestBarContent({ status, requestNumber, obj: protocolSetupData })
        );
      } else if (protocolNumber) {
        setTabIndex(0);
      } else {
        history.push("/protocol-setup/browser");
      }
    }
  }, [
    requestNumber,
    requestStatus,
    protocolNumber,
    isLoading,
    requestType
  ]);



  const doTabAction = (index) => {
    log("Tab changed:", index);
    if (!isTabDirty || isFormReadOnly) {
      setTabIndex(index);
    } else {
      lastFocusedElement.current = document.activeElement !== document.body
        ? document.activeElement
        : document.querySelector(`[role="tab"]:nth-child(${tabIndex + 1})`);
      setModal(true);
    }
  }
  if (rqNum && isLoading) {
    return null
  }

  const onSuccess = async () => {
    await fetchPsData();
  };

  return (
    <div className="card-wrapper MuiPaper-elevation1 p-4 rounded">
      <InactiveRequestRedirect requestStatus={requestStatus} />
      <h1 className="my-0 content-page-title" aria-level="1">
        {(requestNumber ? `Protocol ${protocolNumber} Setup Request ${requestNumber}` : `New Protocol ${protocolNumber} Setup Request`)}
      </h1>
      <div className='mt-1'>
        OPEN/Rave Setup forms initiate the development of a new protocol in OPEN and Rave or to update an Eligibility Checklist for an existing protocol.
        <br />
        All required fields must be completed to save each page.
      </div>
      <div className="align-center my-3">
        <div className="d-inline-flex align-items-center justify-content-center bg-white shadow-sm round py-1 px-3" style={{ minWidth: '10rem' }}>
          <RequestActionBar
            content={requestBarContent || []}
            isSubmitFormEnabled={isSubmitFormEnabled}
            formPartType="OPEN_RAVE"
            history={history}
            fetchPsData={fetchPsData}
            requestNumber={requestNumber}
            onSuccess={onSuccess}
            protocolNumber={protocolNumber}
            lpo={protocolSetupData?.lpo}
            requestStatus={requestStatus}
          />
        </div>
      </div>
      <Tabs className="ctsuTabs-wrapper" forceRenderTabPanel selectedIndex={tabIndex} onSelect={doTabAction}>
        <TabList className="ctsuTabs-tabs">
          <Tab className="ctsuTabs-tab" >Protocol Information</Tab>
          <Tab className="ctsuTabs-tab" disabled={!enabledTabs}>
            OPEN Setup
          </Tab>
          <Tab className="ctsuTabs-tab" disabled={!enabledTabs}>
            Rave Setup
          </Tab>
        </TabList>
        <TabPanel className="ctsuTabs-tabPanel">
          <div className={`ctsuTabs-content ${tabIndex === 0 ? 'active' : 'd-none'}`}>
            <InfoForm
              selectedRequest={protocolSetupData}
              requestNumber={requestNumber}
              setDirtyTab={setDirtyTab}
              setEnabledTabs={setEnabledTabs}
              isFormReadOnly={isFormReadOnly || !canEditOpenRaveRequest}
              fetchPsData={fetchPsData}
              isSubmitFormEnabled={isSubmitFormEnabled}

            />
          </div>
        </TabPanel>
        <TabPanel className="ctsuTabs-tabPanel">
          <div className={`ctsuTabs-content ${tabIndex === 1 ? 'active' : 'd-none'}`}>
            <OpenSetupForm
              selectedRequest={protocolSetupData}
              requestNumber={requestNumber}
              setDirtyTab={setDirtyTab}
              isFormReadOnly={isFormReadOnly || !canEditOpenRaveRequest}
              fetchPsData={fetchPsData}
              isSubmitFormEnabled={isSubmitFormEnabled }
            />
          </div>
        </TabPanel>
        <TabPanel className="ctsuTabs-tabPanel">
          <div className={`ctsuTabs-content ${tabIndex === 2 ? 'active' : 'd-none'}`}>
            <RaveSetupForm
              selectedRequest={protocolSetupData}
              requestNumber={requestNumber}
              setDirtyTab={setDirtyTab}
              isFormReadOnly={isFormReadOnly || !canEditOpenRaveRequest}
              fetchPsData={fetchPsData}
              isSubmitFormEnabled={isSubmitFormEnabled }
            />
          </div>
        </TabPanel>
      </Tabs>
      {canSignoffView && isFormReadOnly && <SignOffForms protocolSetupData={protocolSetupData} fetchPsData={fetchPsData} />}
      {openModal && <FormIsDirtyAlert setModal={setModal} onClose={() => {
        if (lastFocusedElement.current) {
          setTimeout(() => {
            lastFocusedElement.current.focus();
          }, 0);
        }
      }} />}
    </div>
  );
}

export default OpenRaveSetup;